import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { NotificationEventType, NotificationGroup } from '@/hooks/useNotificationPreferences';

export const NOTIFICATIONS_KEY = 'notifications';
const LIST_KEY = [NOTIFICATIONS_KEY, 'list'] as const;
const UNREAD_COUNT_KEY = [NOTIFICATIONS_KEY, 'unread-count'] as const;
export const NOTIFICATION_LIST_LIMIT = 20;
const UNREAD_POLL_MS = 60_000;

export interface InAppNotification {
  id: string;
  eventType: NotificationEventType;
  group: NotificationGroup;
  title: string;
  body: string;
  link: string;
  quoteId?: string | null;
  quoteNumber?: string | null;
  accountName?: string | null;
  createdAt: string;
  readAt?: string | null;
}

export interface NotificationListResponse {
  items: InAppNotification[];
  nextCursor?: string | null;
}

export interface UnreadCountResponse {
  count: number;
}

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => {
      const { data } = await api.get<NotificationListResponse>('/me/notifications', {
        params: { limit: NOTIFICATION_LIST_LIMIT },
      });
      return data;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: async () => {
      const { data } = await api.get<UnreadCountResponse>('/me/notifications/unread-count');
      return data;
    },
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: 'always',
    staleTime: 30_000,
  });
}

interface ReadSnapshot {
  list: NotificationListResponse | undefined;
  count: UnreadCountResponse | undefined;
}

function useReadMutation<TVariables>(
  request: (variables: TVariables) => Promise<unknown>,
  markLocally: (
    list: NotificationListResponse | undefined,
    count: UnreadCountResponse | undefined,
    variables: TVariables,
    readAt: string,
  ) => ReadSnapshot,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onMutate: async (variables): Promise<ReadSnapshot> => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: LIST_KEY }),
        queryClient.cancelQueries({ queryKey: UNREAD_COUNT_KEY }),
      ]);
      const previous: ReadSnapshot = {
        list: queryClient.getQueryData<NotificationListResponse>(LIST_KEY),
        count: queryClient.getQueryData<UnreadCountResponse>(UNREAD_COUNT_KEY),
      };
      const next = markLocally(previous.list, previous.count, variables, new Date().toISOString());
      if (next.list) queryClient.setQueryData(LIST_KEY, next.list);
      if (next.count) queryClient.setQueryData(UNREAD_COUNT_KEY, next.count);
      return previous;
    },
    onError: (_error, _variables, previous) => {
      if (previous?.list) queryClient.setQueryData(LIST_KEY, previous.list);
      if (previous?.count) queryClient.setQueryData(UNREAD_COUNT_KEY, previous.count);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] }),
  });
}

export function useMarkNotificationRead() {
  return useReadMutation<string>(
    (id) => api.post(`/me/notifications/${encodeURIComponent(id)}/read`),
    (list, count, id, readAt) => {
      const wasUnread = list?.items.some((item) => item.id === id && !item.readAt) ?? false;
      return {
        list: list && {
          ...list,
          items: list.items.map((item) => (item.id === id && !item.readAt ? { ...item, readAt } : item)),
        },
        count: count && wasUnread ? { count: Math.max(0, count.count - 1) } : count,
      };
    },
  );
}

export function useMarkAllNotificationsRead() {
  return useReadMutation<void>(
    () => api.post('/me/notifications/read-all'),
    (list, count, _variables, readAt) => ({
      list: list && { ...list, items: list.items.map((item) => (item.readAt ? item : { ...item, readAt })) },
      count: count && { count: 0 },
    }),
  );
}
